import { isAxiosError } from 'axios'

import { useDashboardFeatureFlags } from '@components'
import { AlertModal, ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { errorMessageSelector } from '@fourthwall/utils/dist/selectors'
import { useQueryClient } from '@tanstack/react-query'

import logoSrc from '../../../assets/signets/discord.svg'
import type { ConnectBarProps } from '../../../components/ConnectBar/ConnectBar'
import { useOAuthDialog } from '../../../hooks/useOAuthDialog'
import { useCreateDiscordIntegrationMutation } from '../queries/useCreateDiscordIntegrationMutation'
import { useDeleteDiscordIntegrationMutation } from '../queries/useDeleteDiscordIntegrationMutation'
import { useDiscordIntegrationQuery } from '../queries/useDiscordIntegrationQuery'
import { useDiscordRolesQuery } from '../queries/useDiscordRolesQuery'

export const useDiscord = () => {
  const { open } = useModalContext()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const queryClient = useQueryClient()

  const { discordIntegrationQuery, discordIntegrationQueryKey } = useDiscordIntegrationQuery([], {
    enabled: dashboardFeatureFlags.memberships.enabled,
    onError: (error) => {
      if (!isAxiosError(error)) return

      if (error.response?.status === 404) {
        // Clear Discord integration when user removes it
        queryClient.setQueryData([discordIntegrationQueryKey], undefined)
      }

      if (error.response?.status !== 404) {
        const errorMessage = errorMessageSelector(error.response?.data)

        Toasts.notify(errorMessage, { type: 'error' })
      }
    },
  })
  const { createDiscordIntegrationMutation } = useCreateDiscordIntegrationMutation()
  const { deleteDiscordIntegrationMutation } = useDeleteDiscordIntegrationMutation()

  const status =
    !!discordIntegrationQuery.data && !discordIntegrationQuery.isError
      ? ('CONNECTED' as const)
      : ('NOT_CONNECTED' as const)

  const { discordRolesQuery, discordRolesQueryKey } = useDiscordRolesQuery([], {
    enabled: status === 'CONNECTED',
    onError: (error) => {
      if (!isAxiosError(error)) return

      if (error.response?.status === 404) {
        // Clear Discord roles when user removes integration
        queryClient.setQueryData([discordRolesQueryKey], undefined)
      }

      if (error.response?.status !== 404) {
        const errorMessage = errorMessageSelector(error.response?.data)

        Toasts.notify(errorMessage, { type: 'error' })
      }
    },
  })

  const getErrorMessage = (error: string) => {
    switch (error) {
      case 'access_denied':
        return 'Connection access denied. Try connecting again.'
      case 'wrong_bot_permissions':
        return "Fourthwall bot doesn't have correct role permissions set."
      case 'integration_already_exists':
        return 'This connection already exists.'
      case 'unknown_error':
      default:
        return 'Something went wrong. Please try again.'
    }
  }

  const { openOAuthDialog } = useOAuthDialog({
    height: 760,
    onComplete: () => {
      discordIntegrationQuery.refetch()
      discordRolesQuery.refetch()
    },
    onError: (event) => {
      Toasts.notify(getErrorMessage(event.data.description), { type: 'error' })
    },
  })

  const handleConnect = async () => {
    const integration = await createDiscordIntegrationMutation.mutateAsync([])

    openOAuthDialog(integration.redirectTo)
  }

  const handleDisconnect = () => {
    deleteDiscordIntegrationMutation.mutate([], {
      onSuccess: () => {
        discordIntegrationQuery.refetch()
        discordRolesQuery.refetch()

        Toasts.notify('Disconnected from Discord successfully.', { type: 'success' })
      },
      onError: (error) => {
        if (!isAxiosError(error)) return

        if (error.response?.status === 409) {
          return open(AlertModal, {
            title: 'Please remove Discord perk first',
            text: 'Please remove the Discord perk first before removing the server connection.',
            size: 'small',
          })
        }

        const errorMessage = errorMessageSelector(error.response?.data)

        Toasts.notify(errorMessage, { type: 'error' })
      },
    })
  }

  const getConnectBar = (): ConnectBarProps => {
    const connectBarBase = {
      logoSrc,
      description: 'Connect to your Discord account',
      disabled: true,
    }

    if (status === 'NOT_CONNECTED') {
      return {
        ...connectBarBase,
        disabled: discordIntegrationQuery.isFetching,
        onConnect: handleConnect,
      }
    }

    if (status === 'CONNECTED') {
      return {
        ...connectBarBase,
        disabled: discordIntegrationQuery.isFetching,
        onDisconnect: () => {
          open(ConfirmModal, {
            title: 'Are you sure you want to disconnect from Discord?',
            confirmLabel: 'Yes, disconnect',
            confirmAppearance: 'destructive',
            onConfirm: handleDisconnect,
          })
        },
      }
    }

    return connectBarBase
  }

  return {
    discord: {
      status,
      roles: discordRolesQuery.data?.roles,
      connectBar: getConnectBar(),
      isLoading: discordIntegrationQuery.isFetching || discordRolesQuery.isFetching,
      handleConnect,
      handleDisconnect,
    },
  }
}
